import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminListFilterDropdown,
  AdminMultiSelectFilterDropdown,
  AdminFilterDropdownButton,
  AdminHeaderAction,
  AdminMetadataText,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  adminFilterDateInputClasses,
  AdminFilterChevron,
  AdminToolbarLabel,
  ADMIN_LIST_FILTER_MENU_CLASS,
  AdminRowMenuIcon,
  AdminIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';

const Restrictions = () => {
  const navigate = useNavigate();
  
  // Grid template columns for table alignment
  // Restriction ID, Account, Account Type, Restriction Type, Reason, Source, Applied By, Start Date, Review/End Date, Status, Actions
  const gridTemplateColumns = 'minmax(100px, 1fr) minmax(200px, 2.5fr) minmax(100px, 1.2fr) minmax(150px, 1.8fr) minmax(150px, 1.8fr) minmax(180px, 2fr) minmax(120px, 1.5fr) minmax(140px, 1.6fr) minmax(140px, 1.6fr) minmax(100px, 1.2fr) minmax(80px, 1fr)';
  
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    restrictionStatus: 'Active', // Radio: 'Active' (default), 'Lifted', 'Expired'
    accountType: null, // Radio: null (All), 'Customer', 'Business'
    restrictionType: [], // Multi-select checkboxes
    reason: [], // Multi-select checkboxes
    source: [], // Multi-select checkboxes
    dateFrom: '',
    dateTo: '',
    appliedBy: null // Radio: null (All), admin names, 'System'
  });
  
  // Dropdown states
  const [statusOpen, setStatusOpen] = useState(false);
  const [accountTypeOpen, setAccountTypeOpen] = useState(false);
  const [restrictionTypeOpen, setRestrictionTypeOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [appliedByOpen, setAppliedByOpen] = useState(false);
  
  // Refs for click outside
  const statusRef = useRef(null);
  const accountTypeRef = useRef(null);
  const restrictionTypeRef = useRef(null);
  const reasonRef = useRef(null);
  const sourceRef = useRef(null);
  const dateRangeRef = useRef(null);
  const appliedByRef = useRef(null);
  
  // Click outside handlers
  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(accountTypeRef, () => setAccountTypeOpen(false));
  useClickOutside(restrictionTypeRef, () => setRestrictionTypeOpen(false));
  useClickOutside(reasonRef, () => setReasonOpen(false));
  useClickOutside(sourceRef, () => setSourceOpen(false));
  useClickOutside(dateRangeRef, () => setDateRangeOpen(false));
  useClickOutside(appliedByRef, () => setAppliedByOpen(false));

  const [restrictions, setRestrictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [sortField, setSortField] = useState('startDate');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Row actions state
  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const rowActionsRefs = useRef({});

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle clicking outside to close row actions dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutside = Object.keys(rowActionsRefs.current).every(restrictionId => {
        const el = rowActionsRefs.current[restrictionId];
        return !el || !el.contains(event.target);
      });

      if (clickedOutside) {
        setRowActionsOpen({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    if (filterName === 'restrictionStatus' || filterName === 'accountType' || filterName === 'appliedBy') {
      setFilters(prev => ({ ...prev, [filterName]: value }));
    } else if (filterName === 'restrictionType' || filterName === 'reason' || filterName === 'source') {
      setFilters(prev => ({
        ...prev,
        [filterName]: prev[filterName].includes(value)
          ? prev[filterName].filter(item => item !== value)
          : [...prev[filterName], value]
      }));
    } else if (filterName === 'dateFrom') {
      setFilters(prev => ({ ...prev, dateFrom: value }));
    } else if (filterName === 'dateTo') {
      setFilters(prev => ({ ...prev, dateTo: value }));
    }
    setCurrentPage(1);
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Toggle row actions dropdown
  const toggleRowActions = (restrictionId) => {
    setRowActionsOpen((prev) => {
      if (prev[restrictionId]) return {};
      return { [restrictionId]: true };
    });
  };

  // Handle row action clicks
  const handleRowAction = (action, restriction) => {
    const restrictionId = restriction.restrictionId || restriction.id;
    setRowActionsOpen(prev => ({ ...prev, [restrictionId]: false }));
    
    switch (action) {
      case 'viewRestriction':
        navigate(`/admin/fraud/restrictions/${restrictionId}`, { state: { restriction } });
        break;
      case 'modifyRestriction':
        navigate(`/admin/fraud/restrictions/${restrictionId}?action=modify`, { state: { restriction } });
        break;
      case 'liftRestriction':
        navigate(`/admin/fraud/restrictions/${restrictionId}?action=lift`, { state: { restriction } });
        break;
      default:
        break;
    }
  };

  // Fixed dummy restrictions data - consistent for testing
  const generateMockRestrictions = () => {
    const now = new Date();
    const restrictions = [];

    // Fixed restrictions data array - includes all possible status and type combinations
    // IMPORTANT: Mix all statuses together so first page shows variety
    const fixedRestrictions = [
      // Active Customer Restrictions
      { restrictionId: 'R-0101', accountId: 'C-1177', accountName: 'Mark Jones', accountType: 'Customer', profilePicture: 'https://i.pravatar.cc/150?img=1', restrictionType: 'Messaging Blocked', reason: 'Messaging scam / spam', source: 'Fraud Alerts (FR-000421)', appliedBy: 'Sarah Nguyen', daysAgo: 2, reviewDays: null, status: 'Active' },
      { restrictionId: 'R-0102', accountId: 'C-2056', accountName: 'Jane Smith', accountType: 'Customer', restrictionType: 'Login Blocked', reason: 'Multi-account / device farm', source: 'Security Incident (INC-0012)', appliedBy: 'John Smith', daysAgo: 5, reviewDays: null, status: 'Active' },
      { restrictionId: 'R-0103', accountId: 'C-3124', accountName: 'Robert Brown', accountType: 'Customer', restrictionType: 'Quotes Blocked', reason: 'Quote scam / job scam', source: 'Moderation (MC-00421)', appliedBy: 'Sarah Nguyen', daysAgo: 8, reviewDays: 15, status: 'Active' },
      { restrictionId: 'R-0104', accountId: 'C-4567', accountName: 'Emily Davis', accountType: 'Customer', restrictionType: 'Temporary Suspension', reason: 'Fraud investigation', source: 'Fraud Alerts (FR-000389)', appliedBy: 'System', daysAgo: 12, reviewDays: 30, status: 'Active' },
      
      // Active Business Restrictions
      { restrictionId: 'R-0105', accountId: 'B-209', accountName: 'Melbourne Plumbing Co.', accountType: 'Business', restrictionType: 'Posting Blocked', reason: 'Quote scam / job scam', source: 'Fraud Alerts (FR-000421)', appliedBy: 'System', daysAgo: 3, reviewDays: 7, status: 'Active' },
      { restrictionId: 'R-0106', accountId: 'B-312', accountName: 'Quick Fix Tiling', accountType: 'Business', restrictionType: 'Payments Blocked', reason: 'Payment abuse / Chargeback', source: 'Fraud Alerts (FR-000389)', appliedBy: 'Sarah Nguyen', daysAgo: 10, reviewDays: 5, status: 'Active' },
      { restrictionId: 'R-0107', accountId: 'B-445', accountName: 'Apex Plumbing', accountType: 'Business', restrictionType: 'Visibility Hidden', reason: 'Identity mismatch', source: 'Moderation (MC-00421)', appliedBy: 'John Smith', daysAgo: 15, reviewDays: null, status: 'Active' },
      { restrictionId: 'R-0108', accountId: 'B-556', accountName: 'Bolt Electrical', accountType: 'Business', restrictionType: 'Badges Hidden', reason: 'Licence / document abuse', source: 'Manual Admin', appliedBy: 'Admin User', daysAgo: 20, reviewDays: 10, status: 'Active' },
      { restrictionId: 'R-0109', accountId: 'B-667', accountName: 'Coastal Gas & Heating', accountType: 'Business', restrictionType: 'Verification Locked', reason: 'Licence / document abuse', source: 'Manual Admin', appliedBy: 'Sarah Nguyen', daysAgo: 25, reviewDays: null, status: 'Active' },
      { restrictionId: 'R-0110', accountId: 'B-778', accountName: 'Rapid Roofers', accountType: 'Business', restrictionType: 'Permanent Ban', reason: 'Other policy violation', source: 'Moderation (MC-00422)', appliedBy: 'System', daysAgo: 30, reviewDays: null, status: 'Active' },
      
      // Lifted Restrictions
      { restrictionId: 'R-0078', accountId: 'B-140', accountName: 'City Electrical Pty Ltd', accountType: 'Business', restrictionType: 'Badges Hidden', reason: 'Licence / document abuse', source: 'Manual Admin', appliedBy: 'System', daysAgo: 35, reviewDays: 7, status: 'Lifted' },
      { restrictionId: 'R-0089', accountId: 'C-8890', accountName: 'Michael Wilson', accountType: 'Customer', restrictionType: 'Messaging Blocked', reason: 'Messaging scam / spam', source: 'Fraud Alerts (FR-000300)', appliedBy: 'John Smith', daysAgo: 40, reviewDays: 10, status: 'Lifted' },
      { restrictionId: 'R-0090', accountId: 'B-990', accountName: 'Ace Carpentry', accountType: 'Business', restrictionType: 'Posting Blocked', reason: 'Quote scam / job scam', source: 'Moderation (MC-00400)', appliedBy: 'Sarah Nguyen', daysAgo: 45, reviewDays: 14, status: 'Lifted' },
      { restrictionId: 'R-0091', accountId: 'C-9911', accountName: 'Sarah Johnson', accountType: 'Customer', restrictionType: 'Quotes Blocked', reason: 'Quote scam / job scam', source: 'Fraud Alerts (FR-000250)', appliedBy: 'System', daysAgo: 50, reviewDays: 20, status: 'Lifted' },
      
      // Expired Restrictions
      { restrictionId: 'R-0065', accountId: 'B-112', accountName: 'Modern Painters', accountType: 'Business', restrictionType: 'Temporary Suspension', reason: 'Fraud investigation', source: 'Security Incident (INC-0010)', appliedBy: 'Admin User', daysAgo: 60, reviewDays: 30, status: 'Expired' },
      { restrictionId: 'R-0066', accountId: 'C-2233', accountName: 'David Lee', accountType: 'Customer', restrictionType: 'Login Blocked', reason: 'Multi-account / device farm', source: 'Security Incident (INC-0009)', appliedBy: 'John Smith', daysAgo: 65, reviewDays: 15, status: 'Expired' },
      
      // More Active Restrictions - Mixed
      { restrictionId: 'R-0111', accountId: 'C-3344', accountName: 'Lisa Anderson', accountType: 'Customer', restrictionType: 'Messaging Blocked', reason: 'Messaging scam / spam', source: 'Moderation (MC-00423)', appliedBy: 'Sarah Nguyen', daysAgo: 1, reviewDays: null, status: 'Active' },
      { restrictionId: 'R-0112', accountId: 'B-889', accountName: 'Green Lawn Care', accountType: 'Business', restrictionType: 'Payments Blocked', reason: 'Payment abuse / Chargeback', source: 'Fraud Alerts (FR-000450)', appliedBy: 'System', daysAgo: 4, reviewDays: 14, status: 'Active' },
      { restrictionId: 'R-0113', accountId: 'C-4455', accountName: 'James Taylor', accountType: 'Customer', restrictionType: 'Posting Blocked', reason: 'Other policy violation', source: 'Manual Admin', appliedBy: 'Admin User', daysAgo: 6, reviewDays: 21, status: 'Active' },
      { restrictionId: 'R-0114', accountId: 'B-9900', accountName: 'Smart Locksmith', accountType: 'Business', restrictionType: 'Visibility Hidden', reason: 'Identity mismatch', source: 'Moderation (MC-00424)', appliedBy: 'John Smith', daysAgo: 9, reviewDays: null, status: 'Active' },
      { restrictionId: 'R-0115', accountId: 'C-5566', accountName: 'Amanda White', accountType: 'Customer', restrictionType: 'Quotes Blocked', reason: 'Quote scam / job scam', source: 'Fraud Alerts (FR-000460)', appliedBy: 'Sarah Nguyen', daysAgo: 11, reviewDays: 7, status: 'Active' },
      { restrictionId: 'R-0116', accountId: 'B-1101', accountName: 'Top Tilers', accountType: 'Business', restrictionType: 'Badges Hidden', reason: 'Licence / document abuse', source: 'Manual Admin', appliedBy: 'System', daysAgo: 14, reviewDays: 30, status: 'Active' },
      { restrictionId: 'R-0117', accountId: 'C-6677', accountName: 'Chris Martinez', accountType: 'Customer', restrictionType: 'Verification Locked', reason: 'Identity mismatch', source: 'Security Incident (INC-0013)', appliedBy: 'Admin User', daysAgo: 16, reviewDays: null, status: 'Active' },
      { restrictionId: 'R-0118', accountId: 'B-2202', accountName: 'Clean Windows Pro', accountType: 'Business', restrictionType: 'Temporary Suspension', reason: 'Fraud investigation', source: 'Fraud Alerts (FR-000470)', appliedBy: 'John Smith', daysAgo: 18, reviewDays: 60, status: 'Active' },
      { restrictionId: 'R-0119', accountId: 'C-7788', accountName: 'Patricia Garcia', accountType: 'Customer', restrictionType: 'Login Blocked', reason: 'Multi-account / device farm', source: 'Security Incident (INC-0014)', appliedBy: 'System', daysAgo: 22, reviewDays: null, status: 'Active' },
      { restrictionId: 'R-0120', accountId: 'B-3303', accountName: 'Master Builders', accountType: 'Business', restrictionType: 'Permanent Ban', reason: 'Other policy violation', source: 'Moderation (MC-00425)', appliedBy: 'Sarah Nguyen', daysAgo: 28, reviewDays: null, status: 'Active' },
      
      // More Lifted Restrictions
      { restrictionId: 'R-0092', accountId: 'B-4404', accountName: 'Elite Flooring', accountType: 'Business', restrictionType: 'Payments Blocked', reason: 'Payment abuse / Chargeback', source: 'Fraud Alerts (FR-000200)', appliedBy: 'Admin User', daysAgo: 55, reviewDays: 25, status: 'Lifted' },
      { restrictionId: 'R-0093', accountId: 'C-8899', accountName: 'Thomas Moore', accountType: 'Customer', restrictionType: 'Posting Blocked', reason: 'Other policy violation', source: 'Moderation (MC-00399)', appliedBy: 'John Smith', daysAgo: 58, reviewDays: 28, status: 'Lifted' },
      
      // More Expired Restrictions
      { restrictionId: 'R-0067', accountId: 'B-5505', accountName: 'Perfect Plumbing', accountType: 'Business', restrictionType: 'Visibility Hidden', reason: 'Identity mismatch', source: 'Manual Admin', appliedBy: 'System', daysAgo: 70, reviewDays: 45, status: 'Expired' },
      { restrictionId: 'R-0068', accountId: 'C-9900', accountName: 'Jennifer Harris', accountType: 'Customer', restrictionType: 'Quotes Blocked', reason: 'Quote scam / job scam', source: 'Fraud Alerts (FR-000150)', appliedBy: 'Sarah Nguyen', daysAgo: 75, reviewDays: 30, status: 'Expired' },
    ];

    // Convert fixed data to restriction objects
    fixedRestrictions.forEach((restrictionData) => {
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - restrictionData.daysAgo);
      startDate.setHours(9 + (restrictionData.daysAgo % 12), 11 + (restrictionData.daysAgo % 50), 0, 0);
      
      let reviewDate = null;
      if (restrictionData.reviewDays !== null) {
        reviewDate = new Date(startDate);
        reviewDate.setDate(reviewDate.getDate() + restrictionData.reviewDays);
        reviewDate.setHours(9, 0, 0, 0);
      }

      restrictions.push({
        restrictionId: restrictionData.restrictionId,
        accountId: restrictionData.accountId,
        accountName: restrictionData.accountName,
        accountType: restrictionData.accountType,
        profilePicture: restrictionData.profilePicture || (restrictionData.accountType === 'Customer' ? `https://i.pravatar.cc/150?img=${restrictionData.restrictionId.slice(-2)}` : null),
        restrictionType: restrictionData.restrictionType,
        reason: restrictionData.reason,
        source: restrictionData.source,
        appliedBy: restrictionData.appliedBy,
        startDate: startDate,
        reviewDate: reviewDate,
        status: restrictionData.status
      });
    });

    return restrictions;
  };

  // Format date and time
  const formatDateTime = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) + ' · ' + 
           d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  // Fetch restrictions data
  useEffect(() => {
    const fetchRestrictions = async () => {
      setLoading(true);
      setError(null);
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        const allRestrictions = generateMockRestrictions();

        // Apply search and filters
        let filteredRestrictions = allRestrictions.filter(restriction => {
          const matchesSearch = !debouncedSearch || debouncedSearch.trim().length === 0 || 
            restriction.restrictionId.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
            restriction.accountId.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
            restriction.accountName.toLowerCase().includes(debouncedSearch.toLowerCase());

          const matchesStatus = filters.restrictionStatus === null || restriction.status === filters.restrictionStatus;
          const matchesAccountType = filters.accountType === null || restriction.accountType === filters.accountType;
          const matchesRestrictionType = filters.restrictionType.length === 0 || filters.restrictionType.includes(restriction.restrictionType);
          const matchesReason = filters.reason.length === 0 || filters.reason.includes(restriction.reason);
          const matchesSource = filters.source.length === 0 || filters.source.some(s => restriction.source.includes(s));
          const matchesAppliedBy = filters.appliedBy === null || restriction.appliedBy === filters.appliedBy;

          let matchesDateRange = true;
          if (filters.dateFrom || filters.dateTo) {
            const startDate = new Date(restriction.startDate);
            if (filters.dateFrom && startDate < new Date(filters.dateFrom)) matchesDateRange = false;
            if (filters.dateTo && startDate > new Date(filters.dateTo)) matchesDateRange = false;
          }

          return matchesSearch && matchesStatus && matchesAccountType && matchesRestrictionType && 
                 matchesReason && matchesSource && matchesAppliedBy && matchesDateRange;
        });

        // Apply sorting
        filteredRestrictions.sort((a, b) => {
          let aValue = a[sortField];
          let bValue = b[sortField];

          if (sortField === 'startDate' || sortField === 'reviewDate') {
            aValue = aValue ? new Date(aValue).getTime() : 0;
            bValue = bValue ? new Date(bValue).getTime() : 0;
          }

          if (aValue === null || aValue === undefined) return sortOrder === 'asc' ? 1 : -1;
          if (bValue === null || bValue === undefined) return sortOrder === 'asc' ? -1 : 1;

          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          }
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        });

        const totalCount = filteredRestrictions.length;
        const computedTotalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
        const clampedPage = Math.min(currentPage, computedTotalPages);
        const startIndex = (clampedPage - 1) * CONFIG_LIST_PAGE_SIZE;

        setTotalFilteredCount(totalCount);
        setTotalPages(computedTotalPages);
        setRestrictions(filteredRestrictions.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE));

        if (clampedPage !== currentPage) {
          setCurrentPage(clampedPage);
        }
      } catch (err) {
        setError(err.message || 'Failed to load restrictions');
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchRestrictions();
  }, [debouncedSearch, filters, currentPage, sortField, sortOrder]);

  const effectivePage = Math.min(currentPage, totalPages);
  const rangeStart =
    totalFilteredCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalFilteredCount);
  const showPagination = totalFilteredCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Account Restrictions</AdminPageTitle>

        {/* Search and Filters Row */}
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            {/* Search - Left */}
            <div className="relative w-full max-w-[450px]">
              <AdminSearchField
                type="text"
                placeholder="Search by Customer name, Business name, Email, Account ID, Restriction ID, ABN"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                inputClassName={searchQuery ? '!pr-10' : ''}
                aria-label="Search restrictions"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 inset-y-0 flex items-center justify-center hover:text-primaryblue transition-colors"
                  aria-label="Clear search"
                >
                  <AdminIcon>
                    <i className="fi fi-rs-cross text-sm text-offblack" aria-hidden />
                  </AdminIcon>
                </button>
              )}
            </div>

            {/* Filters - Right */}
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Status"
                value={filters.restrictionStatus}
                options={['Active', 'Lifted', 'Expired'].map((option) => ({
                  value: option,
                  label: option,
                }))}
                onChange={(value) => handleFilterChange('restrictionStatus', value)}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
              <AdminListFilterDropdown
                label="Account Type"
                value={filters.accountType}
                options={[
                  { value: null, label: 'All Account Types' },
                  { value: 'Customer', label: 'Customer' },
                  { value: 'Business', label: 'Business' },
                ]}
                onChange={(value) => handleFilterChange('accountType', value)}
                menuRef={accountTypeRef}
                open={accountTypeOpen}
                setOpen={setAccountTypeOpen}
              />
              <AdminMultiSelectFilterDropdown
                label="Restriction Type"
                emptyLabel="All Restriction Types"
                selected={filters.restrictionType}
                options={[
                  'Login Blocked',
                  'Messaging Blocked',
                  'Posting Blocked',
                  'Quotes Blocked',
                  'Payments Blocked',
                  'Visibility Hidden',
                  'Badges Hidden',
                  'Verification Locked',
                  'Temporary Suspension',
                  'Permanent Ban',
                ]}
                onToggle={(type) => handleFilterChange('restrictionType', type)}
                menuRef={restrictionTypeRef}
                open={restrictionTypeOpen}
                setOpen={setRestrictionTypeOpen}
              />
              <AdminMultiSelectFilterDropdown
                label="Reason"
                emptyLabel="All Reasons"
                selected={filters.reason}
                options={[
                  'Fraud investigation',
                  'Payment abuse / Chargeback',
                  'Messaging scam / spam',
                  'Quote scam / job scam',
                  'Identity mismatch',
                  'Licence / document abuse',
                  'Multi-account / device farm',
                  'Other policy violation',
                ]}
                onToggle={(reason) => handleFilterChange('reason', reason)}
                menuRef={reasonRef}
                open={reasonOpen}
                setOpen={setReasonOpen}
              />
              <AdminMultiSelectFilterDropdown
                label="Source"
                emptyLabel="All Sources"
                selected={filters.source}
                options={['Fraud Alerts', 'Moderation', 'Security Incident', 'Manual Admin']}
                onToggle={(source) => handleFilterChange('source', source)}
                menuRef={sourceRef}
                open={sourceOpen}
                setOpen={setSourceOpen}
              />
              <div className="relative" ref={dateRangeRef}>
                <AdminFilterDropdownButton
                  type="button"
                  onClick={() => setDateRangeOpen(!dateRangeOpen)}
                  aria-expanded={dateRangeOpen}
                  aria-label="Date Range"
                >
                  <span className="truncate">
                    {filters.dateFrom || filters.dateTo
                      ? [filters.dateFrom, filters.dateTo].filter(Boolean).join(' – ')
                      : 'All Date Ranges'}
                  </span>
                  <AdminFilterChevron open={dateRangeOpen} />
                </AdminFilterDropdownButton>
                {dateRangeOpen && (
                  <div className={`${ADMIN_LIST_FILTER_MENU_CLASS} !max-w-[18rem] px-3 py-2 space-y-2`}>
                    <div>
                      <AdminToolbarLabel className="block mb-1">From</AdminToolbarLabel>
                      <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className={adminFilterDateInputClasses()}
                      />
                    </div>
                    <div>
                      <AdminToolbarLabel className="block mb-1">To</AdminToolbarLabel>
                      <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className={adminFilterDateInputClasses()}
                      />
                    </div>
                  </div>
                )}
              </div>
              <AdminListFilterDropdown
                label="Applied By"
                value={filters.appliedBy}
                options={[
                  { value: null, label: 'All Applied By' },
                  ...['System', 'Sarah Nguyen', 'John Smith', 'Admin User'].map((option) => ({
                    value: option,
                    label: option,
                  })),
                ]}
                onChange={(value) => handleFilterChange('appliedBy', value)}
                menuRef={appliedByRef}
                open={appliedByOpen}
                setOpen={setAppliedByOpen}
              />
            </div>
          </div>
        </div>

        {/* Restrictions Table */}
        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-white animate-pulse rounded"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <p className="text-alertred mb-4">{error}</p>
              <AdminHeaderAction variant={"primarysmall"} onClick={() => window.location.reload()}>
                Retry
              </AdminHeaderAction>
            </div>
          ) : restrictions.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-mediumgray">No restrictions match your filters.</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-5" style={{ gridTemplateColumns }}>
                <div className="flex items-center">
                  <button
                    onClick={() => handleSort('restrictionId')}
                    className="flex items-center gap-1"
                  >
                    <span className="font-semibold">Restriction ID</span>
                  </button>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Account</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Account Type</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Restriction Type</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Reason</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Source</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Applied By</span>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={() => handleSort('startDate')}
                    className="flex items-center gap-1"
                  >
                    <span className="font-semibold">Start Date</span>
                  </button>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={() => handleSort('reviewDate')}
                    className="flex items-center gap-1"
                  >
                    <span className="font-semibold">Review / End Date</span>
                  </button>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-1"
                  >
                    <span className="font-semibold">Status</span>
                  </button>
                </div>
                <div className="flex items-center justify-center">
                  <AdminTableHeader>Actions</AdminTableHeader></div>
              </div>

              {/* Rows */}
              {restrictions.map((restriction, index) => {
                const restrictionId = restriction.restrictionId || restriction.id;
                
                return (
                  <div
                    key={restriction.restrictionId || index}
                    className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 cursor-pointer gap-5 hover:bg-gray-50"
                    style={{ gridTemplateColumns }}
                    onClick={() => {
                      // Pass complete restriction data to detail page
                      navigate(`/admin/fraud/restrictions/${restrictionId}`, { 
                        state: { 
                          restriction: {
                            ...restriction,
                            id: restriction.restrictionId,
                            // Ensure all fields are included
                            accountId: restriction.accountId,
                            accountName: restriction.accountName,
                            accountType: restriction.accountType,
                            profilePicture: restriction.profilePicture,
                            restrictionType: restriction.restrictionType,
                            reason: restriction.reason,
                            source: restriction.source,
                            appliedBy: restriction.appliedBy,
                            startDate: restriction.startDate,
                            reviewDate: restriction.reviewDate,
                            status: restriction.status
                          }
                        } 
                      });
                    }}
                  >
                    {/* Restriction ID */}
                    <div className="flex items-center">
                      <span className="whitespace-nowrap text-offblack">
                        {restriction.restrictionId || '—'}
                      </span>
                    </div>
                    
                    {/* Account - With profile picture and name only */}
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 bg-transparent">
                        <img 
                          src={restriction.profilePicture || require('../../../assets/img/User.png')}
                          alt={restriction.accountName}
                          className="w-full h-full object-cover rounded-full"
                          onError={(e) => {
                            e.target.src = require('../../../assets/img/User.png');
                          }}
                        />
                      </div>
                      <span 
                        className="truncate text-offblack"
                        title={restriction.accountName || '—'}
                      >
                        {restriction.accountName || '—'}
                      </span>
                    </div>
                    
                    {/* Account Type */}
                    <div className="flex items-center">
                      <span className="whitespace-nowrap">{restriction.accountType || '—'}</span>
                    </div>
                    
                    {/* Restriction Type */}
                    <div className="flex items-center">
                      <span className="whitespace-nowrap">{restriction.restrictionType || '—'}</span>
                    </div>
                    
                    {/* Reason */}
                    <div className="flex items-center">
                      <span className="whitespace-nowrap">{restriction.reason || '—'}</span>
                    </div>
                    
                    {/* Source */}
                    <div className="flex items-center">
                      <span className="whitespace-nowrap">{restriction.source || '—'}</span>
                    </div>
                    
                    {/* Applied By */}
                    <div className="flex items-center">
                      <span className="whitespace-nowrap">{restriction.appliedBy || '—'}</span>
                    </div>
                    
                    {/* Start Date */}
                    <div className="flex items-center">
                      <span className="whitespace-nowrap">{formatDateTime(restriction.startDate)}</span>
                    </div>
                    
                    {/* Review / End Date */}
                    <div className="flex items-center">
                      <span className="whitespace-nowrap">
                        {restriction.reviewDate ? formatDateTime(restriction.reviewDate) : 'Indefinite'}
                      </span>
                    </div>
                    
                    {/* Status */}
                    <div className="flex items-center">
                      <AdminStatusBadge status={restriction.status} label={restriction.status} />
                    </div>
                    
                    {/* Row Actions */}
                    <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                      <div className="relative inline-flex" ref={el => rowActionsRefs.current[restrictionId] = el}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowActions(restrictionId);
                          }}
                          className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                          title="Row Actions"
                        >
                          <AdminRowMenuIcon />
                        </button>
                        {rowActionsOpen[restrictionId] && (
                          <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                            <div className="py-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowAction('viewRestriction', restriction);
                                }}
                                className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                              >
                                <AdminSupportingText>View Restriction</AdminSupportingText>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowAction('modifyRestriction', restriction);
                                }}
                                className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                              >
                                <AdminSupportingText>Modify Restriction</AdminSupportingText>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowAction('liftRestriction', restriction);
                                }}
                                className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                              >
                                <AdminSupportingText>Lift Restriction</AdminSupportingText>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !error && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalFilteredCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default Restrictions;

